import { useState, useMemo, useEffect } from "react";
import { Search, Users, Download, ChevronUp, ChevronDown } from "lucide-react";
import PageHeader from "../../components/ui/PageHeader";
import { useToast } from "../../hooks/useToast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { format, parse, isValid, startOfMonth, subMonths } from "date-fns";
import api from "../../services/api";
import { ReportDateRangePicker } from "../../components/reports/ReportDateRangePicker";

interface CustomerReportItem {
  id: number;
  name: string;
  email: string;
  phone: string;
  imageUrl: string | null;
  totalBookings: number;
  registeredDate: string;
  registeredDateObj: Date | null;
}

type SortKey = "name" | "email" | "phone" | "totalBookings" | "registeredDate";

const CustomerReportPage = () => {
  const toast = useToast();
  const [customers, setCustomers] = useState<CustomerReportItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>(() => {
    const end = new Date();
    const start = startOfMonth(subMonths(end, 1));
    return { start, end };
  });

  // Stable string keys so the effect only runs when the actual dates change (not on every render)
  const startDateStr = dateRange.start ? format(dateRange.start, "yyyy-MM-dd") : "";
  const endDateStr = dateRange.end ? format(dateRange.end, "yyyy-MM-dd") : "";

  useEffect(() => {
    if (!startDateStr || !endDateStr) {
      setCustomers([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    const fetchCustomers = async () => {
      try {
        setIsLoading(true);
        const response = await api.get("/admin/reports/customer-table", {
          params: { startDate: startDateStr, endDate: endDateStr },
        });
        if (cancelled) return;
        const list = response.data?.customers ?? [];

        const mapped: CustomerReportItem[] = list.map((c: any) => {
          const registeredDateStr = c.registered_date || "";
          let registeredDateObj: Date | null = null;
          if (registeredDateStr) {
            try {
              const parsed = parse(registeredDateStr, "dd MMM yyyy", new Date());
              registeredDateObj = isValid(parsed) ? parsed : null;
            } catch {
              const d = new Date(registeredDateStr);
              registeredDateObj = !isNaN(d.getTime()) ? d : null;
            }
          }
          return {
            id: c.id,
            name: c.name || "—",
            email: c.email || "—",
            phone: c.phone || "—",
            imageUrl: c.image || null,
            totalBookings: c.total_bookings != null ? Number(c.total_bookings) : 0,
            registeredDate: registeredDateStr || "—",
            registeredDateObj,
          };
        });

        setCustomers(mapped);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching customers:", error);
          toast.error("Failed to load customer report");
          setCustomers([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchCustomers();
    return () => {
      cancelled = true;
    };
  }, [startDateStr, endDateStr]);

  const filteredCustomers = useMemo(() => {
    const list = customers.filter(
      (c) =>
        !searchTerm ||
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.phone && c.phone.includes(searchTerm))
    );

    const mult = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let aVal: string | number = a[sortKey];
      let bVal: string | number = b[sortKey];
      if (sortKey === "totalBookings") {
        aVal = a.totalBookings;
        bVal = b.totalBookings;
      } else if (sortKey === "registeredDate") {
        // Use getTime() when both have dates; treat null/undefined as 0 so they sort to start
        const aTime = a.registeredDateObj?.getTime() ?? 0;
        const bTime = b.registeredDateObj?.getTime() ?? 0;
        return mult * (aTime - bTime);
      }
      if (typeof aVal === "string" && typeof bVal === "string") {
        return mult * aVal.localeCompare(bVal);
      }
      return mult * (Number(aVal) - Number(bVal));
    });

    return list;
  }, [customers, searchTerm, sortKey, sortDir]);

  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCustomers.slice(start, start + itemsPerPage);
  }, [filteredCustomers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateRange.start, dateRange.end]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortKey !== columnKey) return null;
    return sortDir === "asc" ? (
      <ChevronUp className="w-4 h-4 inline-block ml-0.5" />
    ) : (
      <ChevronDown className="w-4 h-4 inline-block ml-0.5" />
    );
  };

  const exportCsv = () => {
    const headers = [
      "#",
      "Customer Name",
      "Email",
      "Phone",
      "Total Bookings",
      "Registered Date",
    ];
    const rows = filteredCustomers.map((c, i) => [
      i + 1,
      c.name,
      c.email,
      c.phone,
      c.totalBookings,
      c.registeredDate,
    ]);
    const csv =
      headers.join(",") +
      "\n" +
      rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customer-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 lg:p-6">
        <div className="flex flex-col w-full h-full">
          <PageHeader
            title="Customer Report"
            description="View customer list with bookings and registration dates"
            icon={Users}
            variant="card"
            className="mb-0"
          />

          <div className="flex flex-col gap-4 py-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex flex-shrink-0">
                <ReportDateRangePicker
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                  onChange={(start, end) => setDateRange({ start, end })}
                />
              </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
              <button
                onClick={exportCsv}
                className="inline-flex items-center justify-center gap-2 h-10 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              <span className="text-sm text-gray-500 font-medium whitespace-nowrap">Show</span>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => {
                  setItemsPerPage(Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[80px] bg-white border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-500 font-medium whitespace-nowrap">per page</span>
              </div>
              <div className="relative w-full lg:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-[14px] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>

          <div className="pb-6">
            <div className="rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-[#885ABB] border-b border-gray-100">
                    <tr className="text-sm font-bold text-white tracking-wide">
                      <th className="px-6 py-4 text-left w-12">#</th>
                      <th className="px-6 py-4 text-left">Customer Image</th>
                      <th className="px-6 py-4 text-left">
                        <button
                          type="button"
                          onClick={() => handleSort("name")}
                          className="inline-flex items-center text-white hover:opacity-90"
                        >
                          Customer Name
                          <SortIcon columnKey="name" />
                        </button>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <button
                          type="button"
                          onClick={() => handleSort("email")}
                          className="inline-flex items-center text-white hover:opacity-90"
                        >
                          Email
                          <SortIcon columnKey="email" />
                        </button>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <button
                          type="button"
                          onClick={() => handleSort("phone")}
                          className="inline-flex items-center text-white hover:opacity-90"
                        >
                          Phone
                          <SortIcon columnKey="phone" />
                        </button>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <button
                          type="button"
                          onClick={() => handleSort("totalBookings")}
                          className="inline-flex items-center text-white hover:opacity-90"
                        >
                          Total Bookings
                          <SortIcon columnKey="totalBookings" />
                        </button>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <button
                          type="button"
                          onClick={() => handleSort("registeredDate")}
                          className="inline-flex items-center text-white hover:opacity-90"
                        >
                          Registered Date
                          <SortIcon columnKey="registeredDate" />
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {isLoading ? (
                      <tr>
                        <td colSpan={7} className="py-20 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent mb-4" />
                            <p className="text-gray-500 font-medium">Loading customer report...</p>
                          </div>
                        </td>
                      </tr>
                    ) : paginatedCustomers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-20 text-center text-gray-400">
                          <div className="flex flex-col items-center justify-center">
                            <Users className="w-12 h-12 mb-3 opacity-20" />
                            <p className="text-lg font-medium text-gray-500">No customers found</p>
                            <p className="text-sm">Try adjusting the search or date range.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedCustomers.map((c, idx) => {
                        const rowNum = (currentPage - 1) * itemsPerPage + idx + 1;
                        return (
                          <tr
                            key={c.id}
                            className="hover:bg-purple-50/30 transition-colors group cursor-default even:bg-gray-50/50"
                          >
                            <td className="px-6 py-4 text-sm text-gray-600">{rowNum}</td>
                            <td className="px-6 py-4">
                              <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0 flex items-center justify-center">
                                {c.imageUrl ? (
                                  <img
                                    src={c.imageUrl}
                                    alt={c.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="text-gray-400 text-lg font-medium">
                                    {c.name.charAt(0).toUpperCase() || "?"}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {c.name}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{c.email}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">{c.phone}</td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {c.totalBookings}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {c.registeredDate}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {!isLoading && totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(currentPage * itemsPerPage, filteredCustomers.length)} of{" "}
                    {filteredCustomers.length} customers
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-1.5 px-3">
                      <span className="text-sm font-bold text-[#885ABB]">{currentPage}</span>
                      <span className="text-sm text-gray-400">/</span>
                      <span className="text-sm text-gray-500">{totalPages}</span>
                    </div>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerReportPage;
